import { Close } from '@mui/icons-material'
import { Dialog } from '@dtx-company/design-system/src'
import { IconButton } from '@mui/material'
import { PlanPurchaseExperienceDialogProps } from './PlanPurchaseExperienceDialogProps'
import {
  PlanPurchaseExperienceMainContentMaxWidth,
  PlanPurchaseExperienceMainContentMinWidth,
  PlanPurchaseExperiencePromoContentFullWidth
} from './PlanPurchaseExperience/constants'
import { PlanPurchaseExperienceProps } from './PlanPurchaseExperience/types'
import { useWindowSize } from '../hooks/useWindowSize'
import React, { forwardRef } from 'react'
import chroma from 'chroma-js'
import dynamic from 'next/dynamic'
import styled from '@emotion/styled'
import theme from '../theme'

const DynamicPlanPurchaseExperience = dynamic<PlanPurchaseExperienceProps>(
  () =>
    import('./PlanPurchaseExperience/PlanPurchaseExperience').then(
      mod => mod.PlanPurchaseExperience
    ),
  {
    ssr: false
  }
)

const DIALOG_GUTTER = 32 // configured in mui, not sure how to get it programatically
const FULL_WIDTH =
  PlanPurchaseExperienceMainContentMinWidth + PlanPurchaseExperiencePromoContentFullWidth
const MAX_WIDTH =
  PlanPurchaseExperienceMainContentMaxWidth + PlanPurchaseExperiencePromoContentFullWidth

const _CloseIconButton = styled(IconButton)`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
`

/**
 * Wraps the PPE component inside of a dialog to be used for in-page upsells so that the
 * user doesn't lose what they're working on.
 *
 * This is a low-level abstraction that has been used in some older global modals
 * and hooks that are being phased out.
 *
 * For better a better abstraction, check out the UpsellModal and its variations.
 * @see UpsellModal
 */
export const PlanPurchaseExperienceDialog = forwardRef<
  HTMLDivElement,
  PlanPurchaseExperienceDialogProps
>(function PlanPurchaseExperienceDialog(props, ref): JSX.Element {
  const windowSize = useWindowSize()

  // TODO: add heap event for user canceling flow? and opening?
  // need to differ closing when finished vs closing early (fields on event)
  // need to monitor when open swaps true vs false since onClose
  // might not close unless props.onClose says it does and there is no
  // onOpen parallel anyhow - kind of complex but doable

  const promoBgDark =
    props.promoBackgroundImageUrl &&
    chroma.valid(props.promoBackgroundImageUrl) &&
    chroma(props.promoBackgroundImageUrl).luminance() < 0.5

  return (
    <Dialog
      ref={ref}
      // Use full screen when very small so stripe CC field renders well
      fullScreen={windowSize.width < PlanPurchaseExperienceMainContentMinWidth}
      fullWidth={true}
      PaperProps={{
        sx: {
          // Use a custom maxWidth calculation so the content always fits nicely
          maxWidth:
            windowSize.width >= FULL_WIDTH + 2 * DIALOG_GUTTER
              ? `${MAX_WIDTH}px`
              : `${PlanPurchaseExperienceMainContentMaxWidth}px`
        }
      }}
      open={props.open}
      onClose={(_event, reason) => {
        if (reason === 'escapeKeyDown') {
          props.onClose()
        }
      }}
    >
      <_CloseIconButton
        aria-label="close"
        onClick={props.onClose}
        sx={{ color: promoBgDark ? theme.colors.primary.white : theme.colors.primary.black }}
      >
        <Close />
      </_CloseIconButton>
      <DynamicPlanPurchaseExperience {...props} showLearnMore={true} />
    </Dialog>
  )
})
